import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { fmtUSD } from "./format";

const CHART_MARGIN = { top: 10, right: 10, left: -20, bottom: 0 };
const AXIS_TICK = { fill: "#6b7c74", fontSize: 10 };
const BAR_CURSOR = { fill: "rgba(201,162,39,0.08)" };
const shortDate = (d) => d.slice(5);

function KpiCard({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="p-6 border border-white/10 bg-white/[0.02]">
      <div className="overline" style={{ color: "#A0B0A8" }}>{label}</div>
      <div className="font-display text-4xl mt-3" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

function ChartTooltip({ active, payload, label, money }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#08100D] border border-[#C9A227]/40 px-3 py-2 text-xs">
      <div className="text-white/60">{label}</div>
      <div className="text-[#C9A227] font-semibold">
        {money ? fmtUSD(payload[0].value) : `${payload[0].value} sign-ups`}
      </div>
    </div>
  );
}

export function AnalyticsCharts({ stats, series }) {
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="admin-kpis">
        <KpiCard testid="kpi-mrr" label="MRR" value={fmtUSD(stats?.mrr)} accent="#C9A227" />
        <KpiCard testid="kpi-revenue" label="Total Revenue" value={fmtUSD(stats?.total_revenue)} accent="#C9A227" />
        <KpiCard testid="kpi-members" label="Members" value={stats?.members ?? "—"} />
        <KpiCard testid="kpi-pro" label="Pro Members" value={stats?.pro_members ?? "—"} />
        <KpiCard testid="kpi-videos" label="Swing Videos" value={stats?.total_videos ?? "—"} />
        <KpiCard testid="kpi-unanswered" label="Unanswered Swings" value={stats?.unanswered_videos ?? "—"} accent={stats?.unanswered_videos > 0 ? "#ef4444" : "#fff"} />
        <KpiCard testid="kpi-lessons" label="Lessons Booked" value={stats?.total_lessons ?? "—"} />
        <KpiCard testid="kpi-confirmed-lessons" label="Confirmed Lessons" value={stats?.confirmed_lessons ?? "—"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="p-6 border border-white/10 bg-white/[0.02]" data-testid="admin-revenue-chart">
          <div className="overline" style={{ color: "#A0B0A8" }}>Revenue · last 30 days</div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9A227" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C9A227" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={shortDate} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip content={<ChartTooltip money />} />
                <Area type="monotone" dataKey="revenue" stroke="#C9A227" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 border border-white/10 bg-white/[0.02]" data-testid="admin-signups-chart">
          <div className="overline" style={{ color: "#A0B0A8" }}>New sign-ups · last 30 days</div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={shortDate} />
                <YAxis allowDecimals={false} tick={AXIS_TICK} />
                <Tooltip content={<ChartTooltip />} cursor={BAR_CURSOR} />
                <Bar dataKey="signups" fill="#1B3A2B" stroke="#C9A227" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}
